/**
 * Accordion Component (JavaScript)
 * Reusable accordion selector component supporting both 'buttons' (grid/chips)
 * and 'list' (1 option per line) display modes.
 *
 * Usage:
 *   // 1. Programmatic rendering
 *   const el = Accordion.render({
 *     attribute: 'Vent Configuration',
 *     label: 'Vent Configuration',
 *     options: ['No Vent', 'Standard Vent', 'Deluxe Dual Vent'],
 *     selectedValue: 'No Vent',
 *     displayType: 'buttons', // or 'list'
 *     onChange: (val, attr) => console.log(attr, val)
 *   });
 *   container.appendChild(el);
 *
 *   // 2. Programmatic selection update
 *   Accordion.select('Vent Configuration', 'Standard Vent');
 */

(function () {
    'use strict';

    if (window._accordionComponentInitialized) return;
    window._accordionComponentInitialized = true;

    function _escapeHtml(str) {
        if (str === null || str === undefined) return '';
        return String(str).replace(/[&<>"']/g, function (m) {
            return {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;'
            }[m];
        });
    }

    function _slugify(str) {
        return String(str || '')
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    /**
     * Center the selected option within the scrollable accordion body
     */
    function _centerSelected(wrapper) {
        if (!wrapper) return;
        let selectedBtn = wrapper.querySelector('.option-btn.selected');
        if (!selectedBtn) {
            const hidden = wrapper.querySelector('.selected-option-value') ||
                (wrapper.dataset?.attribute ? document.getElementById(wrapper.dataset.attribute) : null);
            if (hidden && hidden.value) {
                const buttons = wrapper.querySelectorAll('.option-btn');
                selectedBtn = Array.prototype.find.call(buttons, function (b) {
                    return b.dataset.value === String(hidden.value);
                });
            }
        }
        if (!selectedBtn) return;

        const scrollBox = selectedBtn.closest('.accordion-body-box.is-list-mode, .accordion-body-box') ||
            wrapper.querySelector('.accordion-body-box.is-list-mode, .accordion-body-box');
        if (!scrollBox || scrollBox.scrollHeight <= scrollBox.clientHeight) return;

        const boxRect = scrollBox.getBoundingClientRect();
        const btnRect = selectedBtn.getBoundingClientRect();

        if (boxRect.height <= 0) return;

        const currentRelativeTop = btnRect.top - boxRect.top;
        const targetRelativeTop = (scrollBox.clientHeight - btnRect.height) / 2;
        const scrollDelta = currentRelativeTop - targetRelativeTop;

        if (Math.abs(scrollDelta) > 1) {
            scrollBox.scrollTop += scrollDelta;
        }
    }

    function _scrollWrapperToSelected(wrapper) {
        if (!wrapper) return;
        _centerSelected(wrapper);
        setTimeout(function () { _centerSelected(wrapper); }, 40);
        setTimeout(function () { _centerSelected(wrapper); }, 120);
        setTimeout(function () { _centerSelected(wrapper); }, 250);
        setTimeout(function () { _centerSelected(wrapper); }, 450);
    }

    /**
     * Smooth collapse animation helper replicating Bootstrap's height transition
     * without requiring Bootstrap 5 JS to be loaded.
     */
    function _toggleCollapse(collapseEl, accBtn, forceExpand) {
        if (!collapseEl) return;
        const isShown = collapseEl.classList.contains('show');
        const isCollapsing = collapseEl.classList.contains('collapsing');
        const shouldExpand = forceExpand !== undefined ? forceExpand : (!isShown && !isCollapsing);

        const wrapper = collapseEl.closest('.accordion-wrapper');

        if (shouldExpand) {
            if (isShown) return;

            collapseEl.classList.remove('collapse');
            collapseEl.classList.add('collapsing');
            collapseEl.style.height = '0px';

            if (accBtn) {
                accBtn.setAttribute('aria-expanded', 'true');
                accBtn.classList.add('active');
            }
            if (wrapper) wrapper.classList.add('is-open');

            const scrollHeight = collapseEl.scrollHeight;
            collapseEl.style.height = scrollHeight + 'px';

            if (wrapper) _scrollWrapperToSelected(wrapper);

            let cleanedUp = false;
            const cleanup = function () {
                if (cleanedUp) return;
                cleanedUp = true;
                collapseEl.removeEventListener('transitionend', onEnd);
                collapseEl.classList.remove('collapsing');
                collapseEl.classList.add('collapse', 'show');
                collapseEl.style.height = '';
                if (wrapper) _scrollWrapperToSelected(wrapper);
            };

            const onEnd = function (e) {
                if (e && e.target !== collapseEl) return;
                cleanup();
            };

            collapseEl.addEventListener('transitionend', onEnd);
            setTimeout(cleanup, 450);
        } else {
            if (!isShown && !isCollapsing) return;

            collapseEl.style.height = collapseEl.getBoundingClientRect().height + 'px';
            void collapseEl.offsetHeight;

            collapseEl.classList.add('collapsing');
            collapseEl.classList.remove('collapse', 'show');

            if (accBtn) {
                accBtn.setAttribute('aria-expanded', 'false');
                accBtn.classList.remove('active');
            }
            if (wrapper) wrapper.classList.remove('is-open');

            collapseEl.style.height = '0px';

            let cleanedUp = false;
            const cleanup = function () {
                if (cleanedUp) return;
                cleanedUp = true;
                collapseEl.removeEventListener('transitionend', onEnd);
                collapseEl.classList.remove('collapsing');
                collapseEl.classList.add('collapse');
                collapseEl.style.height = '';
            };

            const onEnd = function (e) {
                if (e && e.target !== collapseEl) return;
                cleanup();
            };

            collapseEl.addEventListener('transitionend', onEnd);
            setTimeout(cleanup, 450);
        }
    }

    const Accordion = {
        /**
         * Render an Option Accordion DOM element
         */
        render: function (opts) {
            opts = opts || {};
            const attribute = opts.attribute || 'option';
            const label = opts.label || attribute;
            const options = opts.options || [];
            const displayType = opts.displayType === 'list' ? 'list' : 'buttons';
            const collapseId = opts.collapseId || ('accordion-collapse-' + _slugify(attribute) + '-' + Math.random().toString(36).substring(2, 7));
            const isExpanded = !!opts.isExpanded;
            const showAllOptions = opts.showAllOptions || 0;
            const inputType = opts.inputType || 'main-product';
            const renderGroup = opts.renderGroup !== false;
            const renderInput = opts.renderInput !== false;
            const wrapperClass = opts.wrapperClass || '';

            // Determine selected value
            let selectedVal = opts.selectedValue;
            if (selectedVal === undefined || selectedVal === null) {
                if (options.length > 0) {
                    const first = options[0];
                    selectedVal = typeof first === 'object' && first !== null ? first.value : first;
                } else {
                    selectedVal = '';
                }
            }

            const headerTitle = opts.initialHeaderTitle || selectedVal || label;

            // Build options HTML
            let optionsHtml = '';
            if (displayType === 'list') {
                optionsHtml = '<div class="accordion-body-box is-list-mode"><div class="option-list d-flex flex-column">';
                options.forEach(function (opt) {
                    const optVal = typeof opt === 'object' && opt !== null ? opt.value : opt;
                    const optLabel = typeof opt === 'object' && opt !== null && opt.label ? opt.label : optVal;
                    const optDesc = typeof opt === 'object' && opt !== null && opt.description ? opt.description : '';
                    const isSelected = String(optVal) === String(selectedVal);

                    optionsHtml += '<button type="button" class="option-btn option-btn-list-item ' + (isSelected ? 'selected' : '') + '"' +
                        ' data-value="' + _escapeHtml(optVal) + '"' +
                        ' data-attribute="' + _escapeHtml(attribute) + '"' +
                        ' title="' + _escapeHtml(optVal) + '">' +
                        '<div class="option-list-item-content">' +
                        '<span class="option-list-item-label">' + _escapeHtml(optLabel) + '</span>' +
                        (optDesc ? '<span class="option-list-item-desc">' + _escapeHtml(optDesc) + '</span>' : '') +
                        '</div>' +
                        '<div class="option-list-item-indicator">' +
                        '<svg class="option-check-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
                        '<polyline points="20 6 9 17 4 12"></polyline>' +
                        '</svg>' +
                        '</div>' +
                        '</button>';
                });
                optionsHtml += '</div></div>';
            } else {
                optionsHtml = '<div class="accordion-body-box"><div class="option-buttons d-flex flex-wrap gap-2">';
                options.forEach(function (opt) {
                    const optVal = typeof opt === 'object' && opt !== null ? opt.value : opt;
                    const optLabel = typeof opt === 'object' && opt !== null && opt.label ? opt.label : optVal;
                    const isSelected = String(optVal) === String(selectedVal);

                    optionsHtml += '<button type="button" class="option-btn justify-content-center ' + (isSelected ? 'selected' : '') + '"' +
                        ' data-value="' + _escapeHtml(optVal) + '"' +
                        ' data-attribute="' + _escapeHtml(attribute) + '"' +
                        ' title="' + _escapeHtml(optVal) + '">' +
                        _escapeHtml(optLabel) +
                        '</button>';
                });
                optionsHtml += '</div></div>';
            }

            const accordionWrapperHtml =
                '<div class="accordion-container ' + (displayType === 'list' ? 'is-list-container' : '') + ' mb-2">' +
                '<div class="accordion-wrapper option-accordion-wrapper ' + _escapeHtml(wrapperClass) + '" data-display-type="' + displayType + '"' +
                (opts.closeOnSelect ? ' data-close-on-select="true"' : '') + '>' +
                '<button type="button" class="option-btn-accordion" data-bs-toggle="collapse"' +
                ' data-attribute-label="' + _escapeHtml(label) + '"' +
                ' data-bs-target="#' + _escapeHtml(collapseId) + '"' +
                ' aria-expanded="' + (isExpanded ? 'true' : 'false') + '"' +
                ' aria-controls="' + _escapeHtml(collapseId) + '">' +
                '<span class="accordion-title-text">' + _escapeHtml(headerTitle) + '</span>' +
                '<svg class="accordion-chevron" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"' +
                ' fill="none" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                '<polyline points="6 9 12 15 18 9"></polyline>' +
                '</svg>' +
                '</button>' +
                '<div id="' + _escapeHtml(collapseId) + '" class="collapse ' + (isExpanded ? 'show' : '') + '">' +
                optionsHtml +
                '</div>' +
                '</div>' +
                '</div>';

            const inputHtml = renderInput
                ? '<input type="hidden" class="selected-option-value" id="' + _escapeHtml(opts.inputId || attribute) + '"' +
                (opts.inputName ? ' name="' + _escapeHtml(opts.inputName) + '"' : '') +
                ' data-type="' + _escapeHtml(inputType) + '"' +
                ' value="' + _escapeHtml(selectedVal) + '">'
                : '';

            const container = document.createElement('div');
            if (renderGroup) {
                container.className = 'option-group option-accordion-group mb-4';
                container.dataset.attribute = attribute;
                container.dataset.showAllOptions = String(showAllOptions);
                container.innerHTML = accordionWrapperHtml + inputHtml;
            } else {
                container.innerHTML = accordionWrapperHtml + inputHtml;
            }

            if (typeof opts.onChange === 'function') {
                container._optionAccordionOnChange = opts.onChange;
            }

            return renderGroup ? container : container.firstElementChild;
        },

        /**
         * Update the selected value in an accordion element or by attribute name
         */
        select: function (target, value) {
            let group = null;
            let hidden = null;
            if (typeof target === 'string') {
                const el = document.getElementById(target);
                hidden = el?.classList?.contains('selected-option-value') ? el : null;
                group = document.querySelector('.option-group[data-attribute="' + target + '"]') ||
                    el?.closest('.option-group, .option-accordion-group, .option-accordion-wrapper, .accordion-wrapper') ||
                    el?.parentElement?.querySelector('.accordion-wrapper') ||
                    el?.parentElement;
            } else if (target instanceof HTMLElement) {
                group = target.closest('.option-group, .option-accordion-group, .option-accordion-wrapper, .accordion-wrapper') || target;
            }

            if (!group) return;

            const buttons = group.querySelectorAll('.option-btn');
            let foundText = '';
            buttons.forEach(function (btn) {
                if (value && btn.dataset.value === String(value)) {
                    btn.classList.add('selected');
                    const labelSpan = btn.querySelector('.option-list-item-label');
                    foundText = labelSpan ? labelSpan.textContent.trim() : (btn.textContent || '').trim();
                } else {
                    btn.classList.remove('selected');
                }
            });

            if (!hidden) {
                hidden = group.querySelector('.selected-option-value') ||
                    (group.dataset?.attribute ? document.getElementById(group.dataset.attribute) : null) ||
                    (typeof target === 'string' ? document.getElementById(target) : null);
            }
            if (hidden) {
                hidden.value = value || '';
            }

            const accBtn = group.querySelector('.option-btn-accordion');
            const titleEl = accBtn?.querySelector('.accordion-title-text') || accBtn?.querySelector('span');
            const label = accBtn?.dataset.attributeLabel || group.dataset?.attribute || '';
            if (titleEl) {
                titleEl.textContent = foundText || value || label;
            }

            if (typeof group._optionAccordionOnChange === 'function') {
                group._optionAccordionOnChange(value, group.dataset?.attribute || '', group);
            }

            if (group.querySelector('.collapse.show')) {
                _scrollWrapperToSelected(group);
            }
        },

        /**
         * Update the accordion header title to match current selection
         */
        updateHeader: function (group) {
            if (!group) return;
            const btn = group.querySelector('.option-btn-accordion');
            const titleEl = btn?.querySelector('.accordion-title-text') || btn?.querySelector('span');
            const hidden = group.querySelector('.selected-option-value');
            const label = btn?.dataset.attributeLabel || group.dataset?.attribute || '';
            if (titleEl) {
                titleEl.textContent = hidden?.value || label;
            }
        },

        /**
         * Initialize accordion handlers in a given root container
         */
        init: function (root) {
            root = root || document;
            root.querySelectorAll('.option-accordion-group, .accordion-group, .option-accordion-wrapper, .accordion-wrapper').forEach(function (el) {
                Accordion.updateHeader(el);
                if (el.querySelector('.collapse.show')) {
                    _scrollWrapperToSelected(el);
                }
            });
        },

        toggleCollapse: _toggleCollapse,
        open: function (collapseEl, accBtn) {
            _toggleCollapse(collapseEl, accBtn, true);
        },
        close: function (collapseEl, accBtn) {
            _toggleCollapse(collapseEl, accBtn, false);
        }
    };

    // ─────────────────────────────────────────────────────────────
    // Global Event Delegation
    // ─────────────────────────────────────────────────────────────
    document.addEventListener('click', function (e) {
        // 1. Handle clicking an option button in an accordion (both buttons mode & list mode)
        const optBtn = e.target.closest('.option-btn');
        if (optBtn) {
            const wrapper = optBtn.closest('.option-accordion-wrapper, .accordion-wrapper');
            if (wrapper) {
                const group = optBtn.closest('.option-group, .option-accordion-group') || wrapper.parentElement;
                if (!optBtn.classList.contains('unavailable') && !optBtn.disabled) {
                    const value = optBtn.dataset.value;
                    const attribute = optBtn.dataset.attribute || group?.dataset.attribute;

                    // Deselect siblings and select this option
                    wrapper.querySelectorAll('.option-btn').forEach(function (b) {
                        b.classList.remove('selected');
                    });
                    optBtn.classList.add('selected');

                    // Update hidden input
                    const hidden = group?.querySelector('.selected-option-value') ||
                        (attribute ? document.getElementById(attribute) : null);
                    if (hidden) {
                        hidden.value = value;
                        hidden.dispatchEvent(new Event('change', { bubbles: true }));
                        hidden.dispatchEvent(new Event('input', { bubbles: true }));
                    }

                    // Update header title text
                    const accBtn = wrapper.querySelector('.option-btn-accordion');
                    const titleEl = accBtn?.querySelector('.accordion-title-text') || accBtn?.querySelector('span');
                    const labelSpan = optBtn.querySelector('.option-list-item-label');
                    const displayText = labelSpan ? labelSpan.textContent.trim() : (optBtn.textContent || '').trim();
                    const label = accBtn?.dataset.attributeLabel || attribute || '';
                    if (titleEl) {
                        titleEl.textContent = displayText || value || label;
                    }

                    // Auto-close if close-on-select is enabled
                    if (wrapper.dataset.closeOnSelect === 'true') {
                        const collapseEl = wrapper.querySelector('.collapse, .collapsing');
                        if (collapseEl) {
                            if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                                const bs = bootstrap.Collapse.getInstance(collapseEl) || new bootstrap.Collapse(collapseEl, { toggle: false });
                                bs.hide();
                            } else {
                                _toggleCollapse(collapseEl, accBtn, false);
                            }
                        }
                    }

                    // Trigger custom callback if attached
                    if (group && typeof group._optionAccordionOnChange === 'function') {
                        group._optionAccordionOnChange(value, attribute, group);
                    }
                }
            }
        }

        // 2. Fallback collapse toggle when Bootstrap 5 JS is not loaded
        const accHeaderBtn = e.target.closest('.option-btn-accordion');
        if (accHeaderBtn) {
            const wrapper = accHeaderBtn.closest('.accordion-wrapper, .option-accordion-wrapper');
            const targetSelector = accHeaderBtn.dataset.bsTarget || accHeaderBtn.getAttribute('href');
            const targetEl = targetSelector ? document.querySelector(targetSelector) : wrapper?.querySelector('.collapse, .collapsing');
            const isExpanding = targetEl ? !targetEl.classList.contains('show') : accHeaderBtn.getAttribute('aria-expanded') !== 'true';
            if (isExpanding && wrapper) {
                _scrollWrapperToSelected(wrapper);
            }

            // Check if Bootstrap 5 native collapse is available
            const hasBs = typeof bootstrap !== 'undefined' && bootstrap.Collapse;
            if (!hasBs) {
                if (targetEl) {
                    e.preventDefault();
                    _toggleCollapse(targetEl, accHeaderBtn);
                }
            }
        }

        // 3. Click outside to dismiss floating list-mode accordions
        if (!e.target.closest('.accordion-wrapper[data-display-type="list"]')) {
            document.querySelectorAll('.accordion-wrapper[data-display-type="list"]').forEach(function (wrapper) {
                const collapseEl = wrapper.querySelector('.collapse.show, .collapsing');
                const accBtn = wrapper.querySelector('.option-btn-accordion');
                if (collapseEl) {
                    if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                        const bs = bootstrap.Collapse.getInstance(collapseEl);
                        if (bs) bs.hide();
                    } else {
                        _toggleCollapse(collapseEl, accBtn, false);
                    }
                }
            });
        }
    });

    document.addEventListener('show.bs.collapse', function (e) {
        const wrapper = e.target.closest('.accordion-wrapper, .option-accordion-wrapper');
        if (wrapper) {
            _scrollWrapperToSelected(wrapper);
        }
    });

    document.addEventListener('shown.bs.collapse', function (e) {
        const wrapper = e.target.closest('.accordion-wrapper, .option-accordion-wrapper');
        if (wrapper) {
            _scrollWrapperToSelected(wrapper);
        }
    });

    // ─────────────────────────────────────────────────────────────
    // Keyboard Navigation & Typeahead Search
    // ─────────────────────────────────────────────────────────────
    let _typeaheadBuffer = '';
    let _typeaheadTimer = null;
    let _lastAccordionWrapper = null;

    function _getOptionText(btn) {
        const labelSpan = btn.querySelector('.option-list-item-label');
        return (labelSpan ? labelSpan.textContent : (btn.textContent || btn.dataset.value || '')).trim().toLowerCase();
    }

    function _findMatchingOption(buttons, buffer, currentFocused) {
        if (!buttons || buttons.length === 0 || !buffer) return null;

        const lowerBuffer = buffer.toLowerCase();

        // Check if buffer is a repeated single character, e.g. "c", "cc", "ccc"
        const isRepeatedChar = lowerBuffer.length > 1 && lowerBuffer.split('').every(function (ch) {
            return ch === lowerBuffer[0];
        });

        if (isRepeatedChar) {
            const char = lowerBuffer[0];
            const matches = buttons.filter(function (btn) {
                return _getOptionText(btn).startsWith(char);
            });
            if (matches.length > 0) {
                const currentIndex = matches.indexOf(currentFocused);
                const nextIndex = (currentIndex + 1) % matches.length;
                return matches[nextIndex];
            }
        }

        // 1. Prefix match
        let match = buttons.find(function (btn) {
            return _getOptionText(btn).startsWith(lowerBuffer);
        });
        if (match) return match;

        // 2. Word boundary match (e.g. "States" in "United States")
        match = buttons.find(function (btn) {
            const words = _getOptionText(btn).split(/\s+/);
            return words.some(function (word) {
                return word.startsWith(lowerBuffer);
            });
        });
        if (match) return match;

        return null;
    }

    function _activateOption(matchedBtn, wrapper) {
        if (!matchedBtn) return;

        // 1. Ensure accordion is open so user sees the item
        const collapseEl = wrapper.querySelector('.collapse, .collapsing');
        const accBtn = wrapper.querySelector('.option-btn-accordion');
        if (collapseEl && !collapseEl.classList.contains('show')) {
            if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                const bs = bootstrap.Collapse.getInstance(collapseEl) || new bootstrap.Collapse(collapseEl, { toggle: false });
                bs.show();
            } else {
                _toggleCollapse(collapseEl, accBtn, true);
            }
        }

        // 2. Scroll the list container so the option is in view
        matchedBtn.scrollIntoView({ block: 'nearest' });
        setTimeout(function () {
            matchedBtn.scrollIntoView({ block: 'nearest' });
        }, 60);

        // 3. Focus the matching button
        matchedBtn.focus();
    }

    document.addEventListener('keydown', function (e) {
        // Do not intercept if inside text inputs, textareas, etc.
        const tag = e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        // Verify focus is within an accordion
        const wrapper = e.target.closest('.accordion-wrapper, .option-accordion-wrapper');
        if (!wrapper) return;

        // 1. Escape key closes open accordion
        if (e.key === 'Escape') {
            const collapseEl = wrapper.querySelector('.collapse.show, .collapsing');
            const accBtn = wrapper.querySelector('.option-btn-accordion');
            if (collapseEl) {
                e.preventDefault();
                if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                    const bs = bootstrap.Collapse.getInstance(collapseEl);
                    if (bs) bs.hide();
                } else {
                    _toggleCollapse(collapseEl, accBtn, false);
                }
                if (accBtn) accBtn.focus();
            }
            return;
        }

        // 2. Arrow navigation (ArrowDown / ArrowUp)
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            const buttons = Array.from(wrapper.querySelectorAll('.option-btn:not(.unavailable):not(:disabled)'));
            if (buttons.length === 0) return;
            e.preventDefault();

            const collapseEl = wrapper.querySelector('.collapse, .collapsing');
            const accBtn = wrapper.querySelector('.option-btn-accordion');
            if (collapseEl && !collapseEl.classList.contains('show')) {
                if (typeof bootstrap !== 'undefined' && bootstrap.Collapse) {
                    const bs = bootstrap.Collapse.getInstance(collapseEl) || new bootstrap.Collapse(collapseEl, { toggle: false });
                    bs.show();
                } else {
                    _toggleCollapse(collapseEl, accBtn, true);
                }
            }

            const currIdx = buttons.indexOf(document.activeElement);
            let nextIdx = 0;
            if (e.key === 'ArrowDown') {
                nextIdx = currIdx >= 0 ? Math.min(currIdx + 1, buttons.length - 1) : 0;
            } else {
                nextIdx = currIdx >= 0 ? Math.max(currIdx - 1, 0) : buttons.length - 1;
            }
            buttons[nextIdx].scrollIntoView({ block: 'nearest' });
            buttons[nextIdx].focus();
            return;
        }

        // 3. Single-character typeahead search
        if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
            // If space is pressed without an existing search buffer, let default action toggle the button
            if (e.key === ' ' && !_typeaheadBuffer) return;

            const buttons = Array.from(wrapper.querySelectorAll('.option-btn:not(.unavailable):not(:disabled)'));
            if (buttons.length === 0) return;

            e.preventDefault();

            if (wrapper !== _lastAccordionWrapper) {
                _typeaheadBuffer = '';
                _lastAccordionWrapper = wrapper;
            }

            clearTimeout(_typeaheadTimer);
            _typeaheadBuffer += e.key.toLowerCase();
            _typeaheadTimer = setTimeout(function () {
                _typeaheadBuffer = '';
            }, 850);

            const matchedBtn = _findMatchingOption(buttons, _typeaheadBuffer, document.activeElement);
            if (matchedBtn) {
                _activateOption(matchedBtn, wrapper);
            }
        }
    });

    // Expose to window
    window.Accordion = Accordion;
    window.OptionAccordion = Accordion;
})();
